import type { Row5, YinshState } from "@cloudnest/yinsh-engine";
import {
  COLS,
  EMPTY_POINTS,
  engineToCanonical,
  getPoint,
  H,
  HIT_RADIUS,
  OX,
  OY,
  POINTS,
  SEGMENTS,
  STARTS,
  W,
} from "../utils/board-geometry";

interface BoardProps {
  readonly gameState: YinshState;
  readonly selectedRingPosition: string | null;
  readonly validDestinations: readonly string[];
  readonly onPositionClick: (position: string) => void;
  readonly disabled?: boolean;
}

/* ----- Piece dimensions (canonical) ----- */
const RING_R = 12;
const RING_STROKE_W = 4;
const MARKER_R = 10;
const HALO_R = 18;
/** Visible faint outline drawn around a selected ring (decorative only). */
const SELECTION_OUTLINE_R = 21;

/* ----- Tray constants (canonical) ----- */
const TRAY_RING_R = 9;
const TRAY_RING_GAP = 22;
const TRAY_INSET = 12;
const TRAY_TOP_Y = 14;

function getPendingRowPositions(rows: readonly Row5[]): ReadonlySet<string> {
  const set = new Set<string>();
  for (const row of rows) {
    for (const pos of row.positions) {
      set.add(pos);
    }
  }
  return set;
}

/** Korean screen-reader label for a board piece, e.g. "E5 흰색 링 (선택됨)". */
function pieceAriaLabel(
  enginePos: string,
  pieceType: "RING" | "MARKER",
  player: "WHITE" | "BLACK",
  state: "" | "selected" | "removable" | "pendingRow",
): string {
  const color = player === "BLACK" ? "검은색" : "흰색";
  const kind = pieceType === "RING" ? "링" : "마커";
  const suffix =
    state === "selected"
      ? " (선택됨)"
      : state === "removable"
        ? " (제거 가능)"
        : state === "pendingRow"
          ? " (점수 후보)"
          : "";
  return `${engineToCanonical(enginePos)} ${color} ${kind}${suffix}`;
}

interface TraySlotProps {
  readonly cx: number;
  readonly cy: number;
  readonly color: "WHITE" | "BLACK";
  readonly filled: boolean;
}

function TraySlot({ cx, cy, color, filled }: TraySlotProps) {
  if (!filled) {
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={TRAY_RING_R}
          fill="none"
          stroke="var(--cn-yinsh-tray-empty-slot)"
          strokeWidth={2.5}
        />
        <circle
          cx={cx}
          cy={cy}
          r={TRAY_RING_R - 3}
          fill="none"
          stroke="var(--cn-yinsh-tray-empty-slot)"
          strokeWidth={0.6}
          opacity={0.6}
        />
      </g>
    );
  }
  const body =
    color === "BLACK"
      ? "var(--cn-yinsh-piece-black)"
      : "var(--cn-yinsh-piece-white)";
  const inner = "var(--cn-yinsh-ring-border)";
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={TRAY_RING_R}
        fill="none"
        stroke={body}
        strokeWidth={3}
      />
      <circle
        cx={cx}
        cy={cy}
        r={TRAY_RING_R + 1.4}
        fill="none"
        stroke={inner}
        strokeWidth={0.5}
      />
      <circle
        cx={cx}
        cy={cy}
        r={TRAY_RING_R - 2}
        fill="none"
        stroke={inner}
        strokeWidth={0.5}
      />
    </g>
  );
}

export function Board({
  gameState,
  selectedRingPosition,
  validDestinations,
  onPositionClick,
  disabled = false,
}: BoardProps) {
  const { board, phase, currentPlayer, pendingRows, players, lastMove } =
    gameState;
  const pendingRowPositions = getPendingRowPositions(pendingRows);

  // Last-move overlay: flipped markers + origin (dashed) + destination (solid),
  // drawn on top of all pieces so nothing occludes them. No connecting arrow —
  // the origin/destination rings and the flipped run convey the move on their
  // own (a 1-space arrow would be hidden under the pieces anyway). Each element
  // is gated on the underlying piece still being on the board, so row-resolution
  // (which nulls scored markers / removes rings) can't leave a phantom overlay.
  const isRemovingRow = phase === "REMOVING_ROW";
  const isSelectingRow = isRemovingRow && pendingRows.length > 0;
  const isRemovingRing = isRemovingRow && pendingRows.length === 0;

  const lastMoveFromP = lastMove ? getPoint(lastMove.from) : null;
  const showLastMoveOrigin =
    lastMove != null &&
    lastMoveFromP != null &&
    board[lastMove.from]?.type === "MARKER" &&
    !(isSelectingRow && pendingRowPositions.has(lastMove.from));

  const lastMoveToP = lastMove ? getPoint(lastMove.to) : null;
  const lastMoveDestCell = lastMove ? board[lastMove.to] : null;
  const showLastMoveDest =
    lastMove != null &&
    lastMoveToP != null &&
    lastMoveDestCell?.type === "RING" &&
    lastMove.to !== selectedRingPosition &&
    !(isRemovingRing && lastMoveDestCell.player === currentPlayer);

  const [white, black] = players;

  // Bottom-most points per column for column letter labels.
  const colLabelPoints: { col: string; cx: number; cy: number }[] = [];
  for (let c = 0; c < 11; c++) {
    const bottomRow = STARTS[c];
    const p = POINTS[COLS[c] + bottomRow];
    if (p) {
      colLabelPoints.push({
        col: COLS[c],
        cx: p.x + OX,
        cy: p.y + OY,
      });
    }
  }

  // Leftmost (smallest x) point per row for row number labels.
  const rowLabelMap = new Map<number, { x: number; y: number }>();
  for (const id of Object.keys(POINTS)) {
    const p = POINTS[id];
    const cur = rowLabelMap.get(p.row);
    if (!cur || p.x < cur.x) {
      rowLabelMap.set(p.row, { x: p.x, y: p.y });
    }
  }

  const handleClick = (enginePos: string) => {
    if (disabled) return;
    onPositionClick(enginePos);
  };

  return (
    <svg
      data-testid="game-board"
      role="img"
      aria-label="YINSH 보드 (85 교차점)"
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{
        display: "block",
        maxWidth: 500,
        margin: "0 auto",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {/* Outer rounded-square frame */}
      <rect
        x={0.5}
        y={0.5}
        width={W - 1}
        height={H - 1}
        rx={10}
        ry={10}
        fill="var(--cn-yinsh-bg)"
        stroke="var(--cn-yinsh-frame-border)"
        strokeWidth={1}
      />

      {/* Lattice */}
      <g stroke="var(--cn-yinsh-line)" strokeWidth={1} strokeLinecap="round">
        {SEGMENTS.map((s, i) => (
          <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />
        ))}
      </g>

      {/* Empty point dots (skip positions occupied by a piece) */}
      <g fill="var(--cn-yinsh-dot)" opacity={0.6}>
        {EMPTY_POINTS.map(({ id, cx, cy }) =>
          board[id] ? null : (
            <circle key={`dot-${id}`} cx={cx} cy={cy} r={1.6} />
          ),
        )}
      </g>

      {/* Valid destination indicators (under pieces) */}
      {validDestinations.map((enginePos) => {
        const p = getPoint(enginePos);
        if (!p) return null;
        return (
          <g key={`valid-${enginePos}`} style={{ pointerEvents: "none" }}>
            <circle
              cx={p.cx}
              cy={p.cy}
              r={14}
              fill="none"
              stroke="var(--cn-yinsh-valid-dest)"
              strokeWidth={1.5}
              opacity={0.45}
            />
            <circle
              cx={p.cx}
              cy={p.cy}
              r={4}
              fill="var(--cn-yinsh-valid-dest)"
              opacity={0.9}
            />
          </g>
        );
      })}

      {/* Pending-row halos */}
      {isSelectingRow &&
        Array.from(pendingRowPositions).map((enginePos) => {
          const p = getPoint(enginePos);
          if (!p) return null;
          return (
            <circle
              key={`row-halo-${enginePos}`}
              cx={p.cx}
              cy={p.cy}
              r={14}
              fill="var(--cn-yinsh-row-highlight)"
              opacity={0.18}
              style={{ pointerEvents: "none" }}
            />
          );
        })}

      {/* Pieces (rings + markers) */}
      {EMPTY_POINTS.map(({ id, cx, cy }) => {
        const cell = board[id];
        if (!cell) return null;

        const isSelected = id === selectedRingPosition;
        const isInPendingRow = pendingRowPositions.has(id);
        const isRemovable =
          isRemovingRing &&
          cell.type === "RING" &&
          cell.player === currentPlayer;

        if (cell.type === "RING") {
          const body =
            cell.player === "BLACK"
              ? "var(--cn-yinsh-piece-black)"
              : "var(--cn-yinsh-piece-white)";
          const innerStroke = "var(--cn-yinsh-ring-border)";
          return (
            // biome-ignore lint/a11y/noInteractiveElementToNoninteractiveRole: an SVG <g> is not interactive; role="img" exposes the piece as a single labelled graphic to AT.
            <g
              key={`ring-${id}`}
              data-testid={`ring-${id}`}
              data-player={cell.player}
              role="img"
              aria-label={pieceAriaLabel(
                id,
                "RING",
                cell.player,
                isSelected ? "selected" : isRemovable ? "removable" : "",
              )}
              style={{ pointerEvents: "none" }}
            >
              {isSelected && (
                <>
                  {/* Soft glow + thicker halo so the picked-up ring reads
                      clearly (was a single 2px stroke, easy to miss). */}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={HALO_R}
                    fill="var(--cn-yinsh-selection)"
                    opacity={0.16}
                  />
                  <circle
                    cx={cx}
                    cy={cy}
                    r={HALO_R}
                    fill="none"
                    stroke="var(--cn-yinsh-selection)"
                    strokeWidth={3}
                  />
                </>
              )}
              {!isSelected && isRemovable && (
                <circle
                  cx={cx}
                  cy={cy}
                  r={HALO_R}
                  fill="none"
                  stroke="var(--cn-yinsh-row-highlight)"
                  strokeWidth={2}
                  strokeDasharray="3 2"
                />
              )}
              <circle
                cx={cx}
                cy={cy}
                r={RING_R}
                fill="none"
                stroke={body}
                strokeWidth={RING_STROKE_W}
              />
              <circle
                cx={cx}
                cy={cy}
                r={RING_R + 2}
                fill="none"
                stroke={innerStroke}
                strokeWidth={0.7}
              />
              <circle
                cx={cx}
                cy={cy}
                r={RING_R - 2}
                fill="none"
                stroke={innerStroke}
                strokeWidth={0.7}
              />
            </g>
          );
        }

        // Marker
        const fill =
          cell.player === "BLACK"
            ? "var(--cn-yinsh-piece-black)"
            : "var(--cn-yinsh-piece-white)";
        const stroke =
          cell.player === "BLACK"
            ? "var(--cn-yinsh-piece-black)"
            : "var(--cn-yinsh-ring-border)";
        return (
          // biome-ignore lint/a11y/noInteractiveElementToNoninteractiveRole: an SVG <g> is not interactive; role="img" exposes the piece as a single labelled graphic to AT.
          <g
            key={`marker-${id}`}
            data-testid={`marker-${id}`}
            data-player={cell.player}
            role="img"
            aria-label={pieceAriaLabel(
              id,
              "MARKER",
              cell.player,
              isInPendingRow && isSelectingRow ? "pendingRow" : "",
            )}
            style={{ pointerEvents: "none" }}
          >
            {isInPendingRow && isSelectingRow && (
              <circle
                cx={cx}
                cy={cy}
                r={12}
                fill="var(--cn-yinsh-row-highlight)"
                opacity={0.35}
              />
            )}
            <circle
              cx={cx}
              cy={cy}
              r={MARKER_R}
              fill={fill}
              stroke={stroke}
              strokeWidth={0.8}
            />
          </g>
        );
      })}

      {/* Last-move overlay — drawn on top of all pieces so nothing occludes it.
          Flipped markers: solid ring + centre pip. Origin: dashed ring.
          Destination: solid bold ring. */}
      {lastMove && (
        <g style={{ pointerEvents: "none" }}>
          {lastMove.flipped.map((flippedId) => {
            // Only if the flipped marker is still on the board (a scored row
            // can null it) and not currently the orange row-selection target.
            if (board[flippedId]?.type !== "MARKER") return null;
            if (isSelectingRow && pendingRowPositions.has(flippedId))
              return null;
            const p = getPoint(flippedId);
            if (!p) return null;
            return (
              <g key={`flip-${flippedId}`}>
                <circle
                  data-testid={`flipped-marker-${flippedId}`}
                  cx={p.cx}
                  cy={p.cy}
                  r={13}
                  fill="none"
                  stroke="var(--cn-yinsh-last-move)"
                  strokeWidth={2.5}
                />
                <circle
                  cx={p.cx}
                  cy={p.cy}
                  r={2.5}
                  fill="var(--cn-yinsh-last-move)"
                />
              </g>
            );
          })}
          {showLastMoveOrigin && lastMoveFromP && (
            <circle
              data-testid={`last-move-origin-${lastMove.from}`}
              cx={lastMoveFromP.cx}
              cy={lastMoveFromP.cy}
              r={11}
              fill="none"
              stroke="var(--cn-yinsh-last-move)"
              strokeWidth={2}
              strokeDasharray="3 3"
            />
          )}
          {showLastMoveDest && lastMoveToP && (
            <circle
              data-testid={`last-move-ring-${lastMove.to}`}
              cx={lastMoveToP.cx}
              cy={lastMoveToP.cy}
              r={16}
              fill="none"
              stroke="var(--cn-yinsh-last-move)"
              strokeWidth={3}
            />
          )}
        </g>
      )}

      {/* Labels */}
      <g
        fill="var(--cn-yinsh-label)"
        fontFamily="var(--cn-font-mono, ui-monospace, monospace)"
        fontSize={10}
        fontWeight={600}
        opacity={0.9}
        style={{ pointerEvents: "none" }}
      >
        {colLabelPoints.map(({ col, cx, cy }) => (
          <text key={`col-${col}`} x={cx} y={cy + 16} textAnchor="middle">
            {col}
          </text>
        ))}
        {Array.from(rowLabelMap.entries()).map(([row, p]) => (
          <text
            key={`row-${row}`}
            x={p.x + OX - 10}
            y={p.y + OY - 2}
            textAnchor="end"
          >
            {row}
          </text>
        ))}
      </g>

      {/* Click hit targets + selection rings */}
      {EMPTY_POINTS.map(({ id, cx, cy }) => {
        const isSelected = id === selectedRingPosition;
        return (
          <g key={`hit-${id}`} data-testid={`intersection-${id}`}>
            {isSelected && (
              <circle
                cx={cx}
                cy={cy}
                r={SELECTION_OUTLINE_R}
                fill="none"
                stroke="var(--cn-yinsh-selection)"
                strokeWidth={2}
                opacity={0.9}
                style={{ pointerEvents: "none" }}
              />
            )}
            <circle
              data-testid={`hit-${id}`}
              cx={cx}
              cy={cy}
              r={HIT_RADIUS}
              fill="transparent"
              cursor={disabled ? "default" : "pointer"}
              onClick={() => handleClick(id)}
            >
              <title>{engineToCanonical(id)}</title>
            </circle>
          </g>
        );
      })}

      {/* Ring trays — top-left (white, ltr), top-right (black, rtl) */}
      <g data-testid="ring-tray-white" style={{ pointerEvents: "none" }}>
        <title>{`${white.ringsScored} of 3 rings captured by white`}</title>
        {[0, 1, 2].map((i) => (
          <TraySlot
            key={i}
            cx={TRAY_INSET + TRAY_RING_R + i * TRAY_RING_GAP}
            cy={TRAY_TOP_Y}
            color="WHITE"
            filled={i < (white.ringsScored ?? 0)}
          />
        ))}
      </g>
      <g data-testid="ring-tray-black" style={{ pointerEvents: "none" }}>
        <title>{`${black.ringsScored} of 3 rings captured by black`}</title>
        {[0, 1, 2].map((i) => (
          <TraySlot
            key={i}
            cx={W - TRAY_INSET - TRAY_RING_R - i * TRAY_RING_GAP}
            cy={TRAY_TOP_Y}
            color="BLACK"
            filled={i < (black.ringsScored ?? 0)}
          />
        ))}
      </g>
    </svg>
  );
}
